import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import type { ImportSummary } from '@app-types/import';
import styles from './ValidationSummary.module.css';

type FilterType = 'all' | 'valid' | 'warning' | 'error' | 'conflict';

interface ValidationSummaryProps {
  summary: ImportSummary;
  activeFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
}

const ValidationSummary: React.FC<ValidationSummaryProps> = ({ summary, activeFilter, onFilterChange }) => {
  const cards = [
    {
      key: 'valid' as FilterType,
      label: 'Valid',
      count: summary.validCount,
      icon: CheckCircle,
      variant: 'valid'
    },
    {
      key: 'warning' as FilterType,
      label: 'Warnings',
      count: summary.warningCount,
      icon: AlertTriangle,
      variant: 'warning'
    },
    {
      key: 'error' as FilterType,
      label: 'Errors',
      count: summary.errorCount,
      icon: XCircle,
      variant: 'error'
    },
    {
      key: 'conflict' as FilterType,
      label: 'Conflicts',
      count: summary.conflictCount,
      icon: RefreshCw,
      variant: 'conflict'
    }
  ];

  return (
    <div className={styles.validationSummary} data-test-id="validation-summary">
      <div className={styles.validationSummaryCards}>
        {cards.map((card) => {
          const Icon = card.icon;
          const isActive = activeFilter === card.key;

          return (
            <button
              key={card.key}
              type="button"
              className={clsx(styles.validationSummaryCard, {
                [styles.validationSummaryCardValid]: card.variant === 'valid',
                [styles.validationSummaryCardWarning]: card.variant === 'warning',
                [styles.validationSummaryCardError]: card.variant === 'error',
                [styles.validationSummaryCardConflict]: card.variant === 'conflict',
                [styles.validationSummaryCardActive]: isActive
              })}
              onClick={() => onFilterChange(isActive ? 'all' : card.key)}
              data-test-id={`validation-card-${card.key}`}
            >
              <div className={styles.validationSummaryCardCount}>{card.count}</div>
              <div className={styles.validationSummaryCardLabel}>
                <Icon size={14} />
                {card.label}
              </div>
            </button>
          );
        })}
      </div>

      <div className={styles.validationSummaryTotals}>
        <strong>Total: {summary.totalRows} rows</strong>
        {' • '}
        <span style={{ color: '#10b981' }}>{summary.createCount} to create</span>
        {' • '}
        <span style={{ color: '#3b82f6' }}>{summary.updateCount} to update</span>
        {' • '}
        <span style={{ color: '#6b7280' }}>{summary.skipCount} to skip</span>
      </div>
    </div>
  );
};

export default ValidationSummary;
